import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

// Components
import { Button, Form, InputNumeric, Loading, Switch, Tooltip, Icons } from "@gpstrackit/gtc-react-controls";
import { NotificationManager } from 'Components/Notifications';
import { Card, CardContent, CardHeader } from '@mui/material';

// Helpers
import { convertToKPH, convertToMIRound } from "Core/data/Helpers";

// Styles
import './style.scss';

// Actions
import { resetPerformanceImprovement, updatePerformanceImprovementConfig } from './redux/slice';

const PerformanceImprovementWidget = ({ configData }) => {
    const dispatch = useDispatch();
    const { messages, locale } = useIntl();
    const formRef = Form.useForm();

    // Redux States
    const { update, loadingUpdate } = useSelector((state) => state.performanceImprovementRedux);
    const user = useSelector((state) => state.securityRedux.user);

    // Local States
    const [data, setData] = useState({
        isEnabled: false,
        avgEvents: '',
        riskScore: '',
        events: '',
        totalDistance: ''
    });
    const [storedMiles, setStoredMiles] = useState(null);
    const [isEditable, setIsEditable] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Functions
    const validateFields = () => {
        if (!data?.isEnabled) {
            return true;
        }
        // Validate only populated fields
        if (data.avgEvents !== '' && data.avgEvents !== null && data.avgEvents !== undefined) {
            const avgEventsVal = parseInt(data.avgEvents);
            if (isNaN(avgEventsVal) || avgEventsVal < 1 || avgEventsVal > 10000) {
                return false;
            }
        }
        if (data.riskScore !== '' && data.riskScore !== null && data.riskScore !== undefined) {
            const riskScoreVal = parseInt(data.riskScore);
            if (isNaN(riskScoreVal) || riskScoreVal < 1 || riskScoreVal > 10000) {
                return false;
            }
        }
        if (data.events !== '' && data.events !== null && data.events !== undefined) {
            const eventsVal = parseInt(data.events);
            if (isNaN(eventsVal) || eventsVal < 1 || eventsVal > 10000) {
                return false;
            }
        }
        if (data.totalDistance !== '' && data.totalDistance !== null && data.totalDistance !== undefined) {
            const totalDistanceVal = parseInt(data.totalDistance);
            if (isNaN(totalDistanceVal) || totalDistanceVal < 1 || totalDistanceVal > 10000) {
                return false;
            }
        }
        return true;
    };

    const handleSave = async () => {
        const config = {
            isEnabled: data?.isEnabled || false
        };

        // Only include populated values
        if (data.avgEvents !== '' && data.avgEvents !== null && data.avgEvents !== undefined) {
            config.avgEvents = parseInt(data.avgEvents);
        }
        if (data.riskScore !== '' && data.riskScore !== null && data.riskScore !== undefined) {
            config.riskScore = parseInt(data.riskScore);
        }
        if (data.events !== '' && data.events !== null && data.events !== undefined) {
            config.events = parseInt(data.events);
        }
        if (data.totalDistance !== '' && data.totalDistance !== null && data.totalDistance !== undefined) {
            config.totalDistance = convertDistanceFromDisplay(parseInt(data.totalDistance));
        }

        const payload = { config };

        try {
            await dispatch(updatePerformanceImprovementConfig(payload));
        } finally {
            setIsSubmitting(false);
        }
    };

    const convertDistanceValue = (value) => {
        // Store value in miles, display in user's preferred unit
        return user?.isKph ? convertToKPH(value) : value;
    };

    const convertDistanceFromDisplay = (displayValue) => {
        // Convert from display value back to miles for storage
        return user?.isKph ? convertToMIRound(displayValue) : displayValue;
    };

    // Effects
    useEffect(() => {
        if (update) {
            if (update?.success) {
                NotificationManager.success(
                    messages['driverPerformanceFunctionTab_performanceImprovement_success'],
                    messages['driverPerformanceFunctionTab_performanceImprovement_updatedConfiguration'],
                    3000,
                );
                setIsEditable(false);
            } else {
                const errorMessage = update?.error || messages['driverPerformanceFunctionTab_performanceImprovement_updatedConfigurationError'];
                NotificationManager.error(
                    messages['driverPerformanceFunctionTab_performanceImprovement_error'],
                    errorMessage,
                    5000,
                );
            }
            dispatch(resetPerformanceImprovement({ update: null }));
        }
    }, [update, dispatch, messages]);

    useEffect(() => {
        if (configData && configData?.jsonValues) {
            try {
                const config = JSON.parse(configData.jsonValues);
                const miles = config?.totalDistance !== undefined ? config.totalDistance : null;
                setStoredMiles(miles);

                setData({
                    isEnabled: configData?.configEnable === 1,
                    avgEvents: config?.avgEvents !== undefined ? config.avgEvents : '',
                    riskScore: config?.riskScore !== undefined ? config.riskScore : '',
                    events: config?.events !== undefined ? config.events : '',
                    totalDistance: miles !== null ? Math.round(convertDistanceValue(miles)) : ''
                });
            } catch (error) {
                console.error('Error parsing performance improvement config:', error);
            }
        }
    }, [configData]);

    useEffect(() => {
        if (storedMiles !== null) {
            setData(prev => ({
                ...prev,
                totalDistance: Math.round(convertDistanceValue(storedMiles))
            }));
        }
    }, [user?.isKph]);

    return (
        <Card className="widget-paper" style={{ position: 'relative' }}>
            {loadingUpdate && <Loading overlay show style={{ position: 'absolute' }} />}
            <CardHeader
                title={
                    <div className="pi-header-content">
                        <div className="header-title">
                            {messages['driverPerformanceFunctionTab_performanceImprovement_performanceImprovement']}
                            <Tooltip
                                className="function-panel-tooltip"
                                placement="bottom"
                                title={messages['driverPerformanceFunctionTab_performanceImprovement_avgEventsTooltip'] || 'Range: 1-10000'}>
                                <Icons name="help" size="small"/>
                            </Tooltip>
                        </div>
                        <div className="header-row">
                            <Switch
                                checked={data?.isEnabled || false}
                                id="piIsEnabled"
                                label={messages['driverPerformanceFunctionTab_performanceImprovement_enableDefaultsPI']}
                                labelPosition="left"
                                name="piIsEnabled"
                                onChange={(value) => {
                                    setData({ ...data, isEnabled: value });
                                    setIsEditable(true);
                                }}
                                size="small"
                            />
                        </div>
                    </div>
                }
            />
            <CardContent>
                <Form
                    ref={formRef.ref}
                    loading={loadingUpdate || isSubmitting}
                    onValidSubmit={async () => {
                        if (validateFields()) {
                            setIsSubmitting(true);
                            await handleSave();
                        }
                    }}
                    className="performance_improvement"
                >
                    <div className="function-panel-section">
                        <div className="metrics-grid">
                            <div className="metric-row">
                                <div className="metric-label">
                                    {messages['driverPerformanceFunctionTab_performanceImprovement_avgEvents']}
                                </div>
                                <div className="metric-input">
                                    <div className="avg-events-input-wrapper">
                                        <InputNumeric
                                            id="piAvgEvents"
                                            value={data.avgEvents}
                                            onChange={(value) => {
                                                setIsEditable(true);
                                                if (value === '' || value === null || value === undefined) {
                                                    setData({...data, avgEvents: ''});
                                                    return;
                                                }
                                                const numValue = parseInt(value);
                                                if (!isNaN(numValue) && numValue >= 1 && numValue <= 10000) {
                                                    setData({...data, avgEvents: numValue});
                                                }
                                            }}
                                            min={1}
                                            max={10000}
                                            isInteger={true}
                                            isDisabled={!data?.isEnabled}
                                            className="avg-events-input"
                                        />
                                        <div className="avg-events-arrows">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const current = !data.avgEvents || data.avgEvents === '' ? 0 : parseInt(data.avgEvents);
                                                    const newValue = current === 0 ? 1 : Math.min(10000, current + 1);
                                                    setData({...data, avgEvents: newValue});
                                                    setIsEditable(true);
                                                }}
                                                className="arrow-button"
                                                disabled={!data?.isEnabled}
                                            >
                                                ▲
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const current = !data.avgEvents || data.avgEvents === '' ? 1 : parseInt(data.avgEvents);
                                                    const newValue = Math.max(1, current - 1);
                                                    setData({...data, avgEvents: newValue});
                                                    setIsEditable(true);
                                                }}
                                                className="arrow-button"
                                                disabled={!data?.isEnabled}
                                            >
                                                ▼
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="metric-row">
                                <div className="metric-label">
                                    {messages['driverPerformanceFunctionTab_performanceImprovement_riskScore']}
                                </div>
                                <div className="metric-input">
                                    <div className="risk-score-input-wrapper">
                                        <InputNumeric
                                            id="piRiskScore"
                                            value={data.riskScore}
                                            onChange={(value) => {
                                                setIsEditable(true);
                                                if (value === '' || value === null || value === undefined) {
                                                    setData({...data, riskScore: ''});
                                                    return;
                                                }
                                                const numValue = parseInt(value);
                                                if (!isNaN(numValue) && numValue >= 1 && numValue <= 10000) {
                                                    setData({...data, riskScore: numValue});
                                                }
                                            }}
                                            min={1}
                                            max={10000}
                                            isInteger={true}
                                            isDisabled={!data?.isEnabled}
                                            className="risk-score-input"
                                        />
                                        <div className="risk-score-arrows">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const current = !data.riskScore || data.riskScore === '' ? 0 : parseInt(data.riskScore);
                                                    const newValue = current === 0 ? 1 : Math.min(10000, current + 1);
                                                    setData({...data, riskScore: newValue});
                                                    setIsEditable(true);
                                                }}
                                                className="arrow-button"
                                                disabled={!data?.isEnabled}
                                            >
                                                ▲
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const current = !data.riskScore || data.riskScore === '' ? 1 : parseInt(data.riskScore);
                                                    const newValue = Math.max(1, current - 1);
                                                    setData({...data, riskScore: newValue});
                                                    setIsEditable(true);
                                                }}
                                                className="arrow-button"
                                                disabled={!data?.isEnabled}
                                            >
                                                ▼
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="metric-row">
                                <div className="metric-label">
                                    {messages['driverPerformanceFunctionTab_performanceImprovement_events']}
                                </div>
                                <div className="metric-input">
                                    <div className="events-input-wrapper">
                                        <InputNumeric
                                            id="piEvents"
                                            value={data.events}
                                            onChange={(value) => {
                                                setIsEditable(true);
                                                if (value === '' || value === null || value === undefined) {
                                                    setData({...data, events: ''});
                                                    return;
                                                }
                                                const numValue = parseInt(value);
                                                if (!isNaN(numValue) && numValue >= 1 && numValue <= 10000) {
                                                    setData({...data, events: numValue});
                                                }
                                            }}
                                            min={1}
                                            max={10000}
                                            isInteger={true}
                                            isDisabled={!data?.isEnabled}
                                            className="events-input"
                                        />
                                        <div className="events-arrows">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const current = !data.events || data.events === '' ? 0 : parseInt(data.events);
                                                    const newValue = current === 0 ? 1 : Math.min(10000, current + 1);
                                                    setData({...data, events: newValue});
                                                    setIsEditable(true);
                                                }}
                                                className="arrow-button"
                                                disabled={!data?.isEnabled}
                                            >
                                                ▲
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const current = !data.events || data.events === '' ? 1 : parseInt(data.events);
                                                    const newValue = Math.max(1, current - 1);
                                                    setData({...data, events: newValue});
                                                    setIsEditable(true);
                                                }}
                                                className="arrow-button"
                                                disabled={!data?.isEnabled}
                                            >
                                                ▼
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="metric-row">
                                <div className="metric-label">
                                    {user?.isKph ? messages['driver_performance_cases_create_kilometersDriven'] : messages['driver_performance_cases_create_milesDriven']}
                                </div>
                                <div className="metric-input">
                                    <div className="total-distance-input-wrapper">
                                        <InputNumeric
                                            id="piTotalDistance"
                                            value={data.totalDistance !== '' && data.totalDistance !== null && data.totalDistance !== undefined ? data.totalDistance : ''}
                                            onChange={(value) => {
                                                setIsEditable(true);
                                                if (value === '' || value === null || value === undefined) {
                                                    setData({...data, totalDistance: ''});
                                                    setStoredMiles(null);
                                                    return;
                                                }
                                                const numValue = parseInt(value);
                                                if (!isNaN(numValue) && numValue >= 1 && numValue <= 10000) {
                                                    setData({...data, totalDistance: numValue});
                                                    setStoredMiles(convertDistanceFromDisplay(numValue));
                                                }
                                            }}
                                            min={1}
                                            max={10000}
                                            isInteger={true}
                                            isDisabled={!data?.isEnabled}
                                            className="total-distance-input"
                                        />
                                        <div className="total-distance-arrows">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const current = !data.totalDistance || data.totalDistance === '' ? 0 : parseInt(data.totalDistance);
                                                    const newValue = current === 0 ? 1 : Math.min(10000, current + 1);
                                                    setData({...data, totalDistance: newValue});
                                                    setStoredMiles(convertDistanceFromDisplay(newValue));
                                                    setIsEditable(true);
                                                }}
                                                className="arrow-button"
                                                disabled={!data?.isEnabled}
                                            >
                                                ▲
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const current = !data.totalDistance || data.totalDistance === '' ? 1 : parseInt(data.totalDistance);
                                                    const newValue = Math.max(1, current - 1);
                                                    setData({...data, totalDistance: newValue});
                                                    setStoredMiles(convertDistanceFromDisplay(newValue));
                                                    setIsEditable(true);
                                                }}
                                                className="arrow-button"
                                                disabled={!data?.isEnabled}
                                            >
                                                ▼
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="widget-footer">
                            <Button
                                variant="primary"
                                onClick={() => formRef?.submit()}
                                isDisabled={!isEditable || !validateFields()}
                                className="save-button"
                            >
                                {messages['driverPerformanceFunctionTab_performanceImprovement_save']}
                            </Button>
                        </div>
                    </div>
                </Form>
            </CardContent>
        </Card>
    );
};

export default PerformanceImprovementWidget;
